// Libraries
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

// MUI
import { Stack } from "@mui/material";

// Components

import Title from "@Settings/components/SystemSettings/common/Title";

import StoreSelector from "./components/StoreSelector";

// Services
import { getKmsStores } from "@/Settings/api/AdministrationTools/queries";

// Types
import { IKmsStore } from "@/Settings/types/administrationTools/model";
import { TSelectedKiosk } from "@/Settings/types/administrationTools/utils";

// Utils
import {
	defineKiosksTypes,
	isAnyKioskSelected,
	joinStores,
	updateSelectedKiosksInStores,
} from "@/Settings/helpers/AdministrationTools/DealerAdministrationTools/KeyManagementSystem";
import { or } from "@/Common/helpers/sonar";
import ActionButton from "./components/ActionButton";
import KioskSelector from "./components/KioskSelector";
import DialogSwitchableKiosks from "./components/DialogSwitchableKiosks";

type StoreAndKioskConfigurationProps = {
	isLoading: boolean;
	kmsStores: IKmsStore[];
	providerDBProperty: string;
	onUpdateStoreAndKioskConfiguration: (value: IKmsStore[]) => void;
};

export default function StoreAndKioskConfiguration(props: Readonly<StoreAndKioskConfigurationProps>) {
	const locationId = useSelector((state: RootState) => state.location.locationId);

	const { providerDBProperty, isLoading: isLoadingAdministrationTools, kmsStores, onUpdateStoreAndKioskConfiguration } = props;

	const [isOpenViewAssignmentDialog, setIsOpenViewAssignmentDialog] = useState(false);

	// Queries
	const {
		data: kmsStoresFromProvider = [],
		isFetching: isFetchingKmsStores,
		refetch: loadKmsStores,
	} = useQuery({
		queryKey: ["oneMicroKmsStores", providerDBProperty, locationId],
		queryFn: async () => {
			if (!providerDBProperty || !locationId) {
				return [];
			}

			const response = (await getKmsStores(locationId, providerDBProperty)) ?? [];
			return defineKiosksTypes(response);
		},
		enabled: false,
		staleTime: Infinity,
	});

	// UseMemos
	const unionKmsStores = useMemo(() => {
		return joinStores(kmsStoresFromProvider, kmsStores);
	}, [kmsStoresFromProvider, kmsStores]);

	// Handlers
	function onSelectKiosks(selectedKiosks: TSelectedKiosk[]) {
		const newKmsStores = updateSelectedKiosksInStores(kmsStores, selectedKiosks);

		onUpdateStoreAndKioskConfiguration(newKmsStores);
	}

	return (
		<Stack spacing={2} component={"article"}>
			<Title title={"Store and Kiosk Configuration"} />
			<Stack spacing={4} direction={"row"}>
				<StoreSelector
					isLoading={or(isLoadingAdministrationTools, isFetchingKmsStores)}
					kmsStores={unionKmsStores}
					selectedKmsStores={kmsStores}
					onChange={onUpdateStoreAndKioskConfiguration}
				/>
				<ActionButton text={"Load Stores"} disabled={isFetchingKmsStores} onClick={loadKmsStores} />
				<KioskSelector
					isLoading={or(isLoadingAdministrationTools, isFetchingKmsStores)}
					stores={kmsStores}
					onChange={onSelectKiosks}
				/>
				<ActionButton
					text={"View Assignments"}
					disabled={!isAnyKioskSelected(kmsStores) || isLoadingAdministrationTools || isFetchingKmsStores}
					onClick={() => setIsOpenViewAssignmentDialog(true)}
				/>
				{isOpenViewAssignmentDialog && (
					<DialogSwitchableKiosks
						key={"dialog-switchable-kiosks"}
						open={isOpenViewAssignmentDialog}
						kmsStores={kmsStores}
						setOpen={setIsOpenViewAssignmentDialog}
						onChange={onUpdateStoreAndKioskConfiguration}
					/>
				)}
			</Stack>
		</Stack>
	);
}
